import {
  ArrowsRightLeftIcon,
  CreditCardIcon,
  EnvelopeIcon,
  LightBulbIcon,
  ReceiptRefundIcon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline'
import { NavLink } from 'react-router'
import LandingFooter from '../landing/LandingFooter'
import LandingHeader from '../landing/LandingHeader'

const SUPPORT_EMAIL = 'support@speechagenda.com'

const topics = [
  {
    name: 'Billing & payments',
    id: 'topic-billing',
    description:
      'Plans, invoices, and payment details live in Club admin → Billing. Email if a charge looks wrong or that page will not load.',
    icon: CreditCardIcon,
    subject: 'Billing question',
  },
  {
    name: 'Refunds',
    id: 'topic-refunds',
    description:
      'Charged for a month your club did not use? Send what happened and it gets sorted out.',
    icon: ReceiptRefundIcon,
    subject: 'Refund request',
  },
  {
    name: 'Something’s not working',
    id: 'topic-bug',
    description:
      'Report a bug or anything that looks broken - a screenshot makes it faster to fix.',
    icon: WrenchScrewdriverIcon,
    subject: 'Bug report',
  },
  {
    name: 'Account & sign-in',
    id: 'topic-account',
    description:
      'Trouble logging in, email sign-in links, or updating your account details.',
    icon: UserCircleIcon,
    subject: 'Account help',
  },
  {
    name: 'Setup & migration',
    id: 'topic-setup',
    description:
      'Starting a club, importing your roster, or moving from FreeToastHost or Easy-Speak.',
    icon: ArrowsRightLeftIcon,
    subject: 'Setup & migration',
  },
  {
    name: 'Feature requests',
    id: 'topic-feedback',
    description:
      'An idea or something your club needs? It shapes what gets built next.',
    icon: LightBulbIcon,
    subject: 'Feature idea',
  },
]

function mailtoHref(subject?: string) {
  return subject
    ? `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`
    : `mailto:${SUPPORT_EMAIL}`
}

export default function Support() {
  return (
    <div className="bg-gray-900">
      <LandingHeader />
      <main>
        <div className="mx-auto max-w-7xl px-6 pb-24 pt-32 sm:pt-40 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="text-base font-semibold leading-7 text-indigo-400">
              Support
            </h1>
            <p className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Every message gets a real answer
            </p>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gray-300">
              A question about billing, a refund, or something that is not
              working? Email support and get a straight answer that actually
              solves it
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-4xl">
            <div className="rounded-3xl bg-white/5 p-8 ring-1 ring-white/10 sm:flex sm:items-center sm:justify-between sm:gap-x-8 xl:p-10">
              <div className="sm:flex sm:items-center sm:gap-x-6">
                <EnvelopeIcon
                  className="hidden h-12 w-12 flex-none text-indigo-400 sm:block"
                  aria-hidden="true"
                />
                <div>
                  <h2 className="text-lg font-semibold leading-8 text-white">
                    Email support
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-gray-300">
                    No ticket queue and no bot. Every club gets the same
                    attention, on the free 3 months or on the $19/month plan
                  </p>
                </div>
              </div>
              <a
                href={mailtoHref()}
                className="mt-6 inline-flex flex-none items-center justify-center rounded-md bg-indigo-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 sm:mt-0">
                {SUPPORT_EMAIL}
              </a>
            </div>
            <p className="mt-6 text-sm leading-6 text-gray-400">
              To get it sorted on the first reply, include your club name, the
              email you use to sign in, and what you were trying to do
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-4xl">
            <h2 className="text-2xl font-bold leading-10 tracking-tight text-white">
              Common topics
            </h2>
            <p className="mt-4 text-base leading-7 text-gray-300">
              Plan changes, payment details, and invoices are self-serve under
              Club admin → Billing. Email works too when something there does
              not
            </p>
            <dl className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {topics.map((topic) => (
                <div
                  key={topic.id}
                  className="flex flex-col rounded-3xl bg-white/5 p-8 ring-1 ring-white/10">
                  <dt>
                    <topic.icon
                      className="h-8 w-8 text-indigo-400"
                      aria-hidden="true"
                    />
                    <span className="mt-4 block text-lg font-semibold leading-8 text-white">
                      {topic.name}
                    </span>
                  </dt>
                  <dd className="mt-2 flex flex-auto flex-col text-sm leading-6 text-gray-300">
                    <p className="flex-auto">{topic.description}</p>
                    <a
                      href={mailtoHref(topic.subject)}
                      className="mt-6 text-sm font-semibold text-indigo-400 hover:text-indigo-300">
                      Send an email <span aria-hidden="true">→</span>
                    </a>
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <p className="mx-auto mt-16 max-w-4xl text-center text-sm leading-6 text-gray-400">
            Looking for quick answers first? Plans and common questions are
            covered on the{' '}
            <NavLink
              to="/pricing"
              className="font-semibold text-indigo-400 hover:text-indigo-300">
              pricing page
            </NavLink>
          </p>
        </div>
      </main>
      <LandingFooter />
    </div>
  )
}
